import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { usePageSeo } from "../lib/siteTexts";
import { COMPANY } from "../lib/constants";

const H2 = ({ children }) => (
  <h2 className="mt-10 text-xl md:text-2xl font-display font-semibold text-petrol">{children}</h2>
);

export default function Privacy() {
  const seo = usePageSeo("privacy");
  useSEO({
    title: seo.title,
    description: seo.description,
    path: "/privacy",
    breadcrumbs: buildBreadcrumbsJsonLd([
      { label: "Home", href: "/" },
      { label: "Privacybeleid", href: "/privacy" },
    ]),
  });

  return (
    <div data-testid="privacy-page" className="container-tight py-14 md:py-20 max-w-3xl">
      <h1 className="text-3xl md:text-4xl font-display font-bold text-petrol">Privacybeleid</h1>
      <p className="mt-4 text-slate-600 leading-relaxed">
        Maas Kozijn hecht veel waarde aan de bescherming van uw persoonsgegevens. In dit privacybeleid leggen
        wij uit welke gegevens wij verwerken, waarom wij dat doen en welke rechten u heeft op grond van de
        Algemene Verordening Gegevensbescherming (AVG).
      </p>

      <H2>Wie is verantwoordelijk?</H2>
      <p className="mt-3 text-slate-600 leading-relaxed">
        Maas Kozijn is de verwerkingsverantwoordelijke voor de verwerking van persoonsgegevens via deze
        website. U kunt ons bereiken via <a className="text-maas underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> of
        telefonisch via {COMPANY.phoneDisplay}.
      </p>

      <H2>Welke gegevens verwerken wij?</H2>
      <ul className="mt-3 space-y-2 text-slate-600 leading-relaxed list-disc pl-5">
        <li>Contactgegevens die u zelf invult in het offerte- of contactformulier: naam, telefoonnummer, e-mailadres, postcode en woonplaats.</li>
        <li>Informatie over uw aanvraag: type aanvraag, aantal kozijnen, gewenste uitvoerperiode en uw bericht.</li>
        <li>Foto's of documenten die u vrijwillig uploadt bij uw aanvraag (bijvoorbeeld foto's van uw huidige kozijnen).</li>
        <li>De pagina waarvandaan u uw aanvraag verstuurde en — indien van toepassing — via welke campagne u ons vond.</li>
        <li>Reviews die u plaatst via een persoonlijke reviewlink (naam, woonplaats, beoordeling en reviewtekst).</li>
      </ul>

      <H2>Waarvoor gebruiken wij uw gegevens?</H2>
      <ul className="mt-3 space-y-2 text-slate-600 leading-relaxed list-disc pl-5">
        <li><strong>Offertes en contact</strong> — om uw aanvraag te beoordelen, contact met u op te nemen en een passende offerte op te stellen (grondslag: uitvoering van een overeenkomst c.q. precontractuele fase).</li>
        <li><strong>Reviews</strong> — om uw ervaring, met uw toestemming, op onze website te tonen (grondslag: toestemming).</li>
        <li><strong>Websiteverbetering</strong> — geanonimiseerde statistieken via Google Analytics, uitsluitend nadat u daarvoor toestemming heeft gegeven via de cookiebanner (grondslag: toestemming).</li>
      </ul>

      <H2>Met wie delen wij gegevens?</H2>
      <p className="mt-3 text-slate-600 leading-relaxed">
        Wij verkopen uw gegevens nooit. Wij gebruiken een beperkt aantal verwerkers die nodig zijn om de
        website en onze dienstverlening te laten werken:
      </p>
      <ul className="mt-3 space-y-2 text-slate-600 leading-relaxed list-disc pl-5">
        <li><strong>Resend</strong> — voor het versturen van e-mailnotificaties over uw aanvraag.</li>
        <li><strong>Hosting- en opslagdiensten</strong> — voor het draaien van de website en het veilig opslaan van geüploade foto's. Geüploade lead-foto's zijn niet publiek toegankelijk.</li>
        <li><strong>Google</strong> — uitsluitend voor analytische statistieken en alleen na uw cookietoestemming.</li>
      </ul>

      <H2>Cookies</H2>
      <p className="mt-3 text-slate-600 leading-relaxed">
        Wij gebruiken noodzakelijke cookies voor de werking van de website. Analytische en marketingcookies
        plaatsen wij alleen na uw toestemming via de cookiebanner. U kunt uw keuze op elk moment aanpassen
        via de knop "Cookievoorkeuren" onderaan de website.
      </p>

      <H2>Hoe lang bewaren wij gegevens?</H2>
      <p className="mt-3 text-slate-600 leading-relaxed">
        Wij bewaren uw gegevens niet langer dan noodzakelijk voor het doel waarvoor ze zijn verzameld.
        Aanvraaggegevens bewaren wij zolang dat nodig is voor de behandeling van uw aanvraag en de eventuele
        daaruit volgende opdracht, en daarna zolang wettelijke (fiscale) bewaarplichten dat vereisen. Wilt u
        dat wij uw gegevens eerder verwijderen? Neem dan contact met ons op.
      </p>

      <H2>Uw rechten</H2>
      <p className="mt-3 text-slate-600 leading-relaxed">U heeft op grond van de AVG het recht op:</p>
      <ul className="mt-3 space-y-2 text-slate-600 leading-relaxed list-disc pl-5">
        <li>inzage in de persoonsgegevens die wij van u verwerken;</li>
        <li>correctie van onjuiste gegevens;</li>
        <li>verwijdering van uw gegevens;</li>
        <li>beperking van de verwerking en bezwaar tegen de verwerking;</li>
        <li>dataportabiliteit (overdracht van uw gegevens);</li>
        <li>het intrekken van eerder gegeven toestemming, zonder dat dit afbreuk doet aan de rechtmatigheid van de verwerking vóór de intrekking.</li>
      </ul>
      <p className="mt-3 text-slate-600 leading-relaxed">
        U kunt een verzoek indienen via <a className="text-maas underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
        Bent u het niet eens met hoe wij met uw gegevens omgaan, dan heeft u het recht een klacht in te
        dienen bij de <a className="text-maas underline" href="https://www.autoriteitpersoonsgegevens.nl" target="_blank" rel="noreferrer">Autoriteit Persoonsgegevens</a>.
      </p>

      <H2>Beveiliging</H2>
      <p className="mt-3 text-slate-600 leading-relaxed">
        Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen, waaronder
        versleutelde verbindingen (HTTPS), afgeschermde opslag van geüploade bestanden en beveiligde toegang
        tot ons klantsysteem.
      </p>

      <p className="mt-10 text-sm text-slate-400">Laatst bijgewerkt: juni 2026. Wij kunnen dit privacybeleid van tijd tot tijd aanpassen; de actuele versie vindt u altijd op deze pagina.</p>
    </div>
  );
}
